import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Download, MessageSquare } from 'lucide-react';
import { Button, Input, Card, Pagination, SortableHeader, AdvancedSearch, type SortDirection } from '../components/ui';
import { API_BASE_URL, getAuthToken } from '../lib/api';

interface Activity {
  id: string;
  activityId: string;
  activityTitle: string;
  accountName: string;
  dealName: string;
  activityDatetime: string;
  activityPurpose: string;
  tsAttendees: string;
}

const SEARCH_FIELDS = [
  { key: 'activityId', label: '商談ID', type: 'text' as const, placeholder: 'ACT-001' },
  { key: 'activityTitle', label: '商談名', type: 'text' as const, placeholder: '商談名...' },
  { key: 'accountName', label: '企業名', type: 'text' as const, placeholder: '企業名...' },
  { key: 'dealName', label: '案件名', type: 'text' as const, placeholder: '案件名...' },
  { key: 'activityPurpose', label: '目的', type: 'text' as const, placeholder: '目的...' },
  { key: 'tsAttendees', label: '参加者', type: 'text' as const, placeholder: '参加者...' },
  { key: 'activityDatetime', label: '商談日', type: 'daterange' as const },
];

export function Activities() {
  const navigate = useNavigate();
  const [activities, setActivities] = useState<Activity[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // 検索条件
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<Record<string, string>>({});
  
  // ページネーション
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(30);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // ソート
  const [sortKey, setSortKey] = useState<string | null>('activityDatetime');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  useEffect(() => {
    fetchActivities();
  }, [page, limit, sortKey, sortDirection]);

  const fetchActivities = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(search && { search }),
        ...(sortKey && sortDirection && { sortBy: sortKey, sortOrder: sortDirection }),
        ...Object.fromEntries(
          Object.entries(filters).filter(([, v]) => v !== '')
        ),
      });
      
      const headers: Record<string, string> = {};
      const token = getAuthToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      
      const response = await fetch(`${API_BASE_URL}/api/activities?${params}`, {
        credentials: 'include',
        headers,
      });
      const data = await response.json();
      
      setActivities(data.data || []);
      setTotalPages(data.pagination?.totalPages || 1);
      setTotal(data.pagination?.total || 0);
    } catch (err) {
      console.error('Fetch activities error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(1);
    fetchActivities();
  };

  const handleReset = () => {
    setSearch('');
    setFilters({});
    setPage(1);
    fetchActivities();
  };

  const handleSort = (key: string, direction: SortDirection) => {
    setSortKey(direction ? key : null);
    setSortDirection(direction);
    setPage(1);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
            商談履歴
          </h1>
          <p style={{ color: 'var(--text-secondary)' }}>
            {total.toLocaleString()} 件の商談
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" leftIcon={<Download size={16} />}>
            CSV出力
          </Button>
          <Button leftIcon={<Plus size={16} />} onClick={() => navigate('/activities/new')}>
            新規作成
          </Button>
        </div>
      </div>

      {/* 検索・フィルター */}
      <Card padding="sm">
        <AdvancedSearch
          fields={SEARCH_FIELDS}
          values={filters}
          onChange={(key, value) => setFilters(prev => ({ ...prev, [key]: value }))}
          onSearch={handleSearch}
          onReset={handleReset}
        >
          <div className="flex-1 min-w-[200px]">
            <Input
              placeholder="商談名・内容で検索..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              leftIcon={<Search size={18} />}
            />
          </div>
          <Button type="button" onClick={handleSearch}>
            検索
          </Button>
        </AdvancedSearch>
      </Card>

      {/* テーブル */}
      <Card padding="none">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <p style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : activities.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64">
            <MessageSquare size={48} className="text-gray-300 mb-4" />
            <p style={{ color: 'var(--text-secondary)' }}>商談がありません</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr>
                  <SortableHeader
                    label="日時"
                    sortKey="activityDatetime"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="商談名"
                    sortKey="activityTitle"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="企業"
                    sortKey="accountName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="案件"
                    sortKey="dealName"
                    currentSortKey={sortKey}
                    currentDirection={sortDirection}
                    onSort={handleSort}
                  />
                  <th className="text-left px-4 py-3 text-sm font-medium" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--light-gray-primary)' }}>
                    目的
                  </th>
                  <th className="text-left px-4 py-3 text-sm font-medium" style={{ color: 'var(--text-secondary)', backgroundColor: 'var(--light-gray-primary)' }}>
                    参加者
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y" style={{ borderColor: 'var(--border-color)' }}>
                {activities.map((activity) => (
                  <tr
                    key={activity.id}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/activities/${activity.id}`)}
                  >
                    <td className="px-4 py-3 whitespace-nowrap" style={{ color: 'var(--text-primary)' }}>
                      {activity.activityDatetime 
                        ? new Date(activity.activityDatetime).toLocaleDateString('ja-JP', {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })
                        : '-'
                      }
                    </td>
                    <td className="px-4 py-3">
                      <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                        {activity.activityTitle || '(タイトルなし)'}
                      </p>
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-primary)' }}>
                      {activity.accountName || '-'}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                      {activity.dealName || '-'}
                    </td>
                    <td className="px-4 py-3 max-w-[200px] truncate" style={{ color: 'var(--text-secondary)' }}>
                      {activity.activityPurpose || '-'}
                    </td>
                    <td className="px-4 py-3" style={{ color: 'var(--text-secondary)' }}>
                      {activity.tsAttendees || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* ページネーション */}
        {!isLoading && total > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onPageChange={setPage}
            onLimitChange={handleLimitChange}
          />
        )}
      </Card>
    </div>
  );
}
